import React, { useContext } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import { PRODUCT_TYPE_MOVIE_REWARDS, PRODUCT_TYPE_VOUCHER_MANAGER } from "@config/config";
import useAccess from "@hooks/useAccess";
import { ApplicationContext } from "@context/ApplicationContext";
import PublicOrAuthRoute from "@components/PublicOrAuthRoute";
import Loader from "@components/Ui/Loader";
import ScrollToTop from "@components/V3/ScrollToTop";
import * as Pages from "@pages";

const noAccess = <Pages.NoPublicAccessPage />;

// Need a full login
const protectedUserRoutes = [
    { path: "/user/profile", element: <Pages.UserProfilePage /> },
    { path: "/user/email-add", element: <Pages.AddEmailPage /> },
    { path: "/user/current-vouchers", element: <Pages.VoucherHistoryPage /> },
    { path: "/user/current-tickets", element: <Pages.HistoryPage /> },
    { path: "/user/membership", element: <Pages.UserMembershipsPage /> },
    { path: "/user/change-password", element: <Pages.ChangePasswordPage /> },
    { path: "/user/my-watchlist", element: <Pages.MyWatchListPage /> },
    { path: "/user/my-offers", element: <Pages.MyOffersPage /> },
];

const authRoutes = [
    { path: "/request-password-reset", element: <Pages.RequestPasswordResetPage /> },
    { path: "/user/create-password/:passwordToken", element: <Pages.CreatePasswordPage /> },
    { path: "/user/forgotten-password/:passwordToken", element: <Pages.ResetForgottenPasswordPage /> },
];

const infoRoutes = [
    { path: "/participating-cinemas", element: <Pages.ParticipatingCinemasPage /> },
    { path: "/faqs", element: <Pages.FaqPage /> },
    { path: "/privacy", element: <Pages.PrivacyPolicyPage /> },
    { path: "/terms", element: <Pages.TermsPage /> },
    { path: "/privacy-collection-notice", element: <Pages.PrivacyCollectionNoticePage /> },
    { path: "/voucher-terms", element: <Pages.VoucherTermsPage /> },
];

// Open to everyone: redirect targets (payment, email verify) and onboarding pages
const commonRoutes = [
    { path: "/lets-get-started", element: <Pages.LetsGetStartedPage /> },
    { path: "/welcome", element: <Pages.WelcomePage /> },
    { path: "/ticket/success/:uuid", element: <Pages.SuccessPage /> },
    { path: "/ticket/payment-result-eway/:uuid", element: <Pages.SuccessPage /> },
    { path: "/movie-club-membership/success/:uuid", element: <Pages.MovieClubSuccessPage /> },
    { path: "/unsuccessful-login", element: <Pages.UnsuccessfulSignInPage /> },
    { path: "/user/email-verify/:token", element: <Pages.EmailVerifyPage /> },
    { path: "/success_book_with_cinema", element: <Pages.SuccessBookWithCinemaPage /> },
];

// Public sites only
const publicSiteRoutes = [
    { path: "/news", element: <Pages.NewsPage /> },
    { path: "/news/:id", element: <Pages.NewsItemPage /> },
    { path: "/about", element: <Pages.AboutUsPage /> },
];

// Require-login and wholesale sites, before a full login

const lockedDownRoutes = [
    ...authRoutes,
    ...infoRoutes,
    { path: "/lets-get-started", element: <Pages.LetsGetStartedPage /> },
    { path: "/welcome", element: <Pages.WelcomePage /> },
];

const renderRoutes = (routes) =>
    routes.map(({ path, element }) => <Route key={path} path={path} element={element} />);

const AppRouter = ({ children }) => (
    <Router>
        <ScrollToTop />
        <Routes>{children}</Routes>
    </Router>
);

const RouteCollection = () => {
    const { whiteLabel, seasonalRoutes, productTypeId, membershipOptions } = useContext(ApplicationContext);
    const {
        isFullyLoggedIn,
        isAppSecurityAuth,
        isPublicSite,
        requiresFullLogin,
        canBrowse,
        canAccessProfile,
    } = useAccess();

    const subscriberGroupRoute = (
        <Route path="/subscriber-group/:groupCode" element={<Pages.SubscriberGroupLandingPage />} />
    );

    if (!whiteLabel?.subscriber_group_id) {
        return (
            <AppRouter>
                {subscriberGroupRoute}
                <Route path="*" element={<Loader isLoading={true} />} />
            </AppRouter>
        );
    }

    const loginElement = isAppSecurityAuth ? <Pages.LoginPage /> : noAccess;

    // Locked-down sites: only login/info pages until fully logged in
    if (requiresFullLogin && !isFullyLoggedIn) {
        return (
            <AppRouter>
                <Route path="/" element={noAccess} />
                {subscriberGroupRoute}
                <Route path="/login" element={loginElement} />
                {renderRoutes(lockedDownRoutes)}
                <Route path="*" element={noAccess} />
            </AppRouter>
        );
    }

    const isMovieRewards = productTypeId === PRODUCT_TYPE_MOVIE_REWARDS;
    const isVoucherManager = productTypeId === PRODUCT_TYPE_VOUCHER_MANAGER;
    const showSeasonal = isMovieRewards && !isPublicSite;
    const isGiftingEnabled = whiteLabel?.enable_gifting === true;
    const hasMembershipOptions = membershipOptions?.length > 0;

    const searchPage = isVoucherManager ? <Pages.VoucherManagerSearchPage /> : <Pages.MovieRewardsSearchPage />;
    const movieDetail = isVoucherManager ? <Pages.NotFoundPage /> : <Pages.MovieDetailPage />;
    const venueDetail = isVoucherManager ? <Pages.NotFoundPage /> : <Pages.VenueDetailPage />;

    return (
        <AppRouter>
            {subscriberGroupRoute}

            {/* Partial access */}
            <Route path="/" element={<PublicOrAuthRoute element={searchPage} />} />
            <Route path="/movies" element={<PublicOrAuthRoute element={<Pages.MoviesPage />} />} />
            <Route path="/cinemas" element={<PublicOrAuthRoute element={<Pages.CinemasPage />} />} />
            <Route path="/ticket/purchase/:id" element={<PublicOrAuthRoute element={<Pages.TicketPurchasePage />} />} />
            <Route path="/movie/:id/:movieName" element={<PublicOrAuthRoute element={movieDetail} />} />
            <Route path="/venue/:id/:venue_name" element={<PublicOrAuthRoute element={venueDetail} />} />
            <Route path="/experiences" element={<PublicOrAuthRoute element={<Pages.ExperiencePage />} />} />
            <Route path="/experiences/:experienceType" element={<PublicOrAuthRoute element={<Pages.ExperiencePage />} />} />
            {isGiftingEnabled && (
                <Route path="/gifting" element={<PublicOrAuthRoute element={<Pages.GiftingPage />} />} />
            )}

            {/* Full login */}
            {protectedUserRoutes.map(({ path, element }) => (
                <Route key={path} path={path} element={canAccessProfile ? element : noAccess} />
            ))}

            {/* Open to everyone */}
            <Route path="/login" element={loginElement} />
            {renderRoutes(authRoutes)}
            {renderRoutes(infoRoutes)}
            {renderRoutes(commonRoutes)}
            {isPublicSite && renderRoutes(publicSiteRoutes)}
            {hasMembershipOptions && <Route path="/movie-club-membership" element={<Pages.MovieClubPage />} />}

            {/*
              * Seasonal advert urls are admin-controlled and may collide with a page above
              * (eg /gifting). These are registered last on purpose.
              * Do not move this block above the routes it must not shadow.
              */}
            {showSeasonal && seasonalRoutes?.filter((route) => route?.advert_url).map((route) => (
                <Route
                    key={route.advert_url}
                    path={route.advert_url}
                    element={<PublicOrAuthRoute element={<Pages.VoucherManagerSearchPage />} />}
                />
            ))}

            <Route path="*" element={canBrowse ? <Pages.NotFoundPage /> : noAccess} />
        </AppRouter>
    );
};

export default RouteCollection;
